import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons"
import { Button, Layout, Menu, message, notification, Select } from "antd"
import { Header } from "antd/es/layout/layout"
import { FC, useEffect, useRef, useState } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { configs } from "~/configs"
import { useLanguage } from "~/languages/LanguageProvider"
import { t } from "~/languages/i18n"
import { doLogoutAction } from "~/modules/auth/services/redux/auth/login.slice"
import { useAppDispatch } from "~/modules/auth/services/redux/hook"
import userService from "~/modules/users/services/userService"
import { Breadcrumbs } from "../Breadcrumbs"
import { LayoutSider } from "./Sider"

const { Content, Footer } = Layout

export const MainLayout: FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [isUserMenuOpen, setUserMenuOpen] = useState(false)
  const { language, changeLanguage } = useLanguage()
  const [userAvatar, setUserAvatar] = useState<string>("")
  const userMenuRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const userServices = userService()

  const user = JSON.parse(localStorage.getItem("user") || "{}")

  const handleUserMenuClick = () => {
    setUserMenuOpen(prev => !prev)
  }

  const handleLogout = () => {
    localStorage.removeItem("access_token")
    localStorage.removeItem("user")
    dispatch(doLogoutAction())
    notification.success({
      message: t("Logout successful"),
      description: t("You have successfully logged out."),
      placement: "topRight",
    })
  }

  const handleProfileClick = () => {
    navigate("users/profile")
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
      setUserMenuOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const fetchUser = async (id: number) => {
    try {
      const response = await userServices.getUserById(id)

      const avatarUrl = response?.avatar?.url
      if (avatarUrl) {
        setUserAvatar(avatarUrl)
      } else {
      }
      return response
    } catch (error) {
      message.error("Failed to fetch user data. Please try again.")
      return null
    }
  }

  useEffect(() => {
    if (user && user.id) {
      fetchUser(user.id)
    } else {
      message.error("No user ID found.")
    }
  }, [])

  return (
    <Layout className="layout" id="mtb-erp-app">
      <LayoutSider collapsed={collapsed} language={language} />
      <Layout>
        <Header className="bg-white p-0 flex justify-between">
          <div>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="text-base w-[64px] h-[64px] p-3"
            />
          </div>
          <div className="relative flex items-center pr-[30px] gap-4" ref={userMenuRef}>
            <Select defaultValue={language} onChange={changeLanguage} className="min-w-24">
              <Select.Option value="vi">Tiếng Việt</Select.Option>
              <Select.Option value="en">English</Select.Option>
            </Select>
            <img
              className="w-8 h-8 rounded-full border-2 border-indigo-400 cursor-pointer"
              src={`${userAvatar ? `${configs.API_URL}${userAvatar}` : "/images/avatar-default.svg"}`}
              alt="User Avatar"
              onClick={handleUserMenuClick}
            />

            {isUserMenuOpen && (
              <Menu
                mode="vertical"
                className="menu-dropdown z-50 absolute right-3 top-full shadow-lg drop-shadow-md px-2 py-1 "
                onClick={handleUserMenuClick}
              >
                <Menu.Item key="profile" onClick={handleProfileClick}>
                  {t("Profile")}
                </Menu.Item>
                <Menu.Item key="logout" onClick={handleLogout}>
                  {t("Logout")}
                </Menu.Item>
              </Menu>
            )}
          </div>
        </Header>
        <Content style={styles.content}>
          <Breadcrumbs />
          <Layout className="site-layout-background rounded-[8px]" style={styles.layout}>
            <Content style={{ padding: "0 12px" }}>
              <Outlet />
            </Content>
          </Layout>
        </Content>
        <Footer style={{ textAlign: "center" }}>{t("Copyright ©2024 - Dan Solutions Co., Ltd")}</Footer>
      </Layout>
    </Layout>
  )
}

const styles = {
  content: { padding: "0 32px", minHeight: "calc(100vh - 134px)" },
  layout: { padding: "12px 0", borderRadius: "0.5rem" },
}
